import { useMemo, useState, useEffect } from 'react';
import { Input, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { createChangeHandler } from 'services/formUtils';
import stringUtils from 'services/stringUtils';
import { SubmitButton } from 'components';
import './CustomLogin.css';

const FetchLimitedInfo = (props: IFetchLimitedInfoProps) => {
  const { t } = useTranslation('translation');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSsoSubmitting, setIsSsoSubmitting] = useState(false);

  const emailIsValid = useMemo(() => stringUtils.validateEmail(email), [email]);

  useEffect(() => {
    props.onEmailChange && props.onEmailChange(email);
  }, [email, props]);

  const onSubmit = () => {
    if (!canBeSubmitted) return;
    setIsSubmitting(true);
    props
      .onSubmit(email)
      .then(
        () => {
          setIsSubmitting(false);
        },
        () => {
          setIsSubmitting(false);
        }
      )
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const onSsoSubmit = async () => {
    setIsSsoSubmitting(true);
    try {
      await props.onSsoSubmit();
    } finally {
      setIsSsoSubmitting(false);
    }
  };

  const canBeSubmitted = useMemo(() => {
    return email.length > 0 && emailIsValid && !isSubmitting;
  }, [isSubmitting, emailIsValid, email]);

  return (
    <>
      {!props.showSsoOption && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Label>
            {t('login.LABEL_EMAIL')}{' '}
            <Input
              name="email"
              autoComplete="username"
              placeholder={t('login.PROMPT_FOR_EMAIL')}
              value={email}
              type="text"
              onChange={createChangeHandler(setEmail)}
            />
          </Label>
          <SubmitButton
            type="submit"
            submitting={isSubmitting}
            disabled={!canBeSubmitted}
            label={t('login.SUBMIT_BUTTON_CONTINUE')}
            activelabel={t('login.SUBMIT_BUTTON_FETCHING_USER_INFO')}
            className="btn-primary"
          />
        </form>
      )}
      {props.showSsoOption && (
        <SubmitButton
          submitting={isSsoSubmitting}
          disabled={false}
          label={t('login.SIGN_IN_WITH_SSO')}
          activelabel={t('login.SIGN_IN_WITH_SSO')}
          className="btn-primary"
          onClick={onSsoSubmit}
        />
      )}
    </>
  );
};

export interface IFetchLimitedInfoProps {
  onSubmit: (email: string) => Promise<void>;
  onSsoSubmit: () => Promise<void>;
  showSsoOption: boolean;
  onEmailChange?: (newValue: string) => void;
}

export default FetchLimitedInfo;
